import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Edit } from "lucide-react";
import { CategoryFilter } from "./CategoryFilter";
import { TagsFilter } from "./TagsFilter";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "convex/react";
import { api } from "../../api";
import { App } from "@/types/app";

interface BulkEditDialogProps {
  selectedApps: App[];
  onClose: () => void;
  onSuccess: () => void;
}

export const BulkEditDialog = ({ selectedApps, onClose, onSuccess }: BulkEditDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  
  const updateAppCategory = useMutation(api.apps.update.category);
  const updateAppTags = useMutation(api.apps.update.tags);

  // Check if all selected apps have the same category
  const allSameCategory = selectedApps.length > 0 && 
    selectedApps.every(app => app.category === selectedApps[0].category);

  const handleSave = async () => {
    if (!selectedCategory && selectedTags.length === 0) {
      toast({
        title: "No changes selected",
        description: "Please select a category or tags to update.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const promises = [];
      
      // Update category if selected
      if (selectedCategory) {
        promises.push(
          ...selectedApps.map(app => 
            updateAppCategory({
              app: app.id as any,
              category: selectedCategory
            })
          )
        );
      }
      
      // Update tags if selected
      if (selectedTags.length > 0) {
        promises.push(
          ...selectedApps.map(app => 
            updateAppTags({
              app: app.id as any,
              tags: selectedTags
            })
          )
        );
      }
      
      await Promise.all(promises);

      toast({
        title: "Apps updated successfully",
        description: `Updated ${selectedApps.length} app${selectedApps.length === 1 ? '' : 's'}`,
      });
      
      setIsOpen(false);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating apps:', error);
      toast({
        title: "Error updating apps",
        description: "Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2">
          <Edit className="w-4 h-4" />
          Edit {selectedApps.length} app{selectedApps.length === 1 ? '' : 's'}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            Bulk Edit {selectedApps.length} App{selectedApps.length === 1 ? '' : 's'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Show selected apps */}
          <div>
            <p className="text-sm text-gray-600 mb-2">Selected apps:</p>
            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
              {selectedApps.map(app => (
                <Badge key={app.id} variant="secondary" className="text-xs">
                  {app.app_name}
                </Badge>
              ))}
            </div>
          </div>

          {/* Category selection */}
          <div>
            <h4 className="font-medium text-sm mb-2">Update Category</h4>
            <CategoryFilter 
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
            />
          </div>

          {/* Tags selection - only show if all apps have same category */}
          {allSameCategory && (
            <div>
              <h4 className="font-medium text-sm mb-2">
                Update Tags
                <span className="text-xs text-gray-500 ml-1">
                  (Available for apps in same category)
                </span>
              </h4>
              <TagsFilter 
                selectedTags={selectedTags}
                onTagsChange={setSelectedTags}
                categoryFilter={selectedApps[0].category}
              />
            </div>
          )}

          {!allSameCategory && selectedApps.length > 1 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                Tag editing is only available when all selected apps are in the same category.
                Selected apps have different categories.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? "Updating..." : "Update Apps"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};